<template>
  <b-dropdown
    ref="dropdown"
    class="app-dropdown"
    :class="{'icon-activator': iconActivator, shadowed, [`size-${size}`]: true, 'has-offset': hasOffset }"
    :variant="variant"
    :size="size"
    v-on="$listeners"
    v-bind="$attrs"
    @hidden="opened = false"
    @shown="onShown"
  >
    <template #button-content>
      <slot name="button-content" v-bind:opened="opened">
        {{ $t($attrs.text) }}
      </slot>
    </template>
    <slot></slot>
  </b-dropdown>
</template>

<script>
export default {
  name: 'AppDropdown',
  props: {
    variant: {
      type: String,
      default: 'outline-secondary'
    },
    iconActivator: {
      type: Boolean,
      default: false
    },
    shadowed: {
      type: Boolean,
      default: false
    },
    size: {
      type: String,
      default: 'md'
    },
    hasOffset: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      opened: false
    }
  },
  methods: {
    async onShown() {
      this.$emit('shown')
      this.opened = true
      // If there is a selected option in the dropdown
      // Always show it in the dropdown viewport when it's opened
      await this.$nextTick()
      const selectedOption = this.$refs.dropdown.$el.querySelector('.selected')
      if (!selectedOption) return
      selectedOption.parentNode.scrollTop = selectedOption.offsetTop - 10
    }
  }
}
</script>

<style scoped lang="scss">
.app-dropdown::v-deep {

  &.shadowed .dropdown-toggle {
    box-shadow: $button-light-gray-shadow;
  }

  li {
    &:not(:last-child) {
      margin-bottom: rem-calc(4)
    }

    &.selected {
      .dropdown-item {
        position: relative;
        color: $primary;
        background: $background-primary 0 0 no-repeat padding-box;

        &:after {
          color: $primary;
          font-weight: 900;
          font-family: "Font Awesome 6 Pro";
          content: "";
          font-size: $font-s;
          background: transparent;
        }
      }
    }
  }

  &.icon-activator .dropdown-toggle {
    padding: 0;
    box-shadow: none;
  }

  &.show .dropdown-toggle.btn-outline-secondary {
    border-color: $light-blue;
    color: $primary;
    box-shadow: $primary-shadow;
  }

  .dropdown-toggle {
    display: inline-flex;
    font-size: $font-14;
    padding-inline-start: .5rem;
    padding-inline-end: .5rem;

    &:active {
      border-color: $light-blue;
      background-color: transparent;
      color: $primary;
      box-shadow: $primary-shadow;
    }

    &:after {
      border-left: rem-calc(5) solid transparent;
      border-right: rem-calc(5) solid transparent;
      border-top: rem-calc(5) solid;
      margin-left: rem-calc(8);
      transform: translateY(2px);
    }
  }

  &.has-offset {
    .dropdown-menu[x-placement^=bottom] {
      top: .5rem !important;
    }

    .dropdown-menu[x-placement^=top] {
      top: -.5rem !important;
    }
  }

  .dropdown-menu {
    background-color: $white;
    @include border-radius(4px);
    box-shadow: $primary-shadow;
    border: 1px solid $light-blue;
    @include active-form-control();
    padding: .5rem 0;
  }

  .dropdown-item {
    text-align: start;
    cursor: pointer;
    font-size: $font-14;
    padding: rem-calc(6 10);
    color: $text-color-gray;
    position: relative;
    background-color: $white;

    i, img {
      margin-right: rem-calc(6);
    }

    &:hover, &:focus {
      color: $primary;
      background-color: $background-primary
    }

    &:not(:last-child) {
      margin-bottom: rem-calc(2);
    }
  }

  &.size-lg {
    .dropdown-toggle {
      font-size: $font-m;
      padding-inline-start: .625rem;
      padding-inline-end: .625rem;
    }

    .dropdown-item {
      padding: .5rem .75rem;
      font-size: $font-m;
    }
  }
}

html[dir="rtl"] {
  .app-dropdown::v-deep {
    .dropdown-toggle {
      &:after {
        margin-right: rem-calc(8);
        margin-left: 0;
      }
    }

    .dropdown-menu.dropdown-menu-right {
      right: auto;
      left: 0;
    }
  }

  .dropdown-item {
    i, img {
      margin-right: 0;
      margin-left: rem-calc(6);
    }
  }
}
</style>
